/**
 * Deterministic pseudo-random number generation. ARCHITECTURE.md §10.
 *
 * Xoshiro128** with 32-bit state, seeded through SplitMix32. Chosen because it is
 * fast, has a long period (2^128-1), passes PractRand, and — critically — has an
 * exactly serialisable integer state, so replays can restore it bit-for-bit.
 *
 * `Math.random()` is banned everywhere in src/. `npm run lint:determinism` enforces it.
 */

/** SplitMix32 — used only to expand a single seed into four state words. */
function splitmix32(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x9e3779b9) >>> 0;
    let t = a;
    t ^= t >>> 16;
    t = Math.imul(t, 0x21f0aaad) >>> 0;
    t ^= t >>> 15;
    t = Math.imul(t, 0x735a2d97) >>> 0;
    t ^= t >>> 15;
    return t >>> 0;
  };
}

function rotl(x, k) {
  return ((x << k) | (x >>> (32 - k))) >>> 0;
}

export class Rng {
  /**
   * @param {number} seed 32-bit unsigned seed.
   * @param {string} label diagnostic label, e.g. 'sim' or 'fx:city'.
   */
  constructor(seed = 1, label = 'rng') {
    this.label = label;
    this.seed = seed >>> 0;
    this.s0 = 0;
    this.s1 = 0;
    this.s2 = 0;
    this.s3 = 0;
    this.draws = 0;
    this.reseed(seed);
  }

  reseed(seed) {
    const sm = splitmix32(seed >>> 0);
    this.seed = seed >>> 0;
    this.s0 = sm();
    this.s1 = sm();
    this.s2 = sm();
    this.s3 = sm();
    // Guard against the (astronomically unlikely) all-zero state.
    if ((this.s0 | this.s1 | this.s2 | this.s3) === 0) this.s0 = 0x9e3779b9;
    this.draws = 0;
    return this;
  }

  /** Raw 32-bit unsigned draw. */
  nextU32() {
    const result = (Math.imul(rotl(Math.imul(this.s1, 5) >>> 0, 7), 9) >>> 0) >>> 0;
    const t = (this.s1 << 9) >>> 0;
    this.s2 = (this.s2 ^ this.s0) >>> 0;
    this.s3 = (this.s3 ^ this.s1) >>> 0;
    this.s1 = (this.s1 ^ this.s2) >>> 0;
    this.s0 = (this.s0 ^ this.s3) >>> 0;
    this.s2 = (this.s2 ^ t) >>> 0;
    this.s3 = rotl(this.s3, 11);
    this.draws++;
    return result;
  }

  /** Uniform float in [0,1). 24 bits of mantissa — plenty, and exactly reproducible. */
  next() {
    return (this.nextU32() >>> 8) / 0x1000000;
  }

  /** Uniform float in [a,b). */
  range(a, b) {
    return a + (b - a) * this.next();
  }

  /** Uniform integer in [a,b] inclusive. */
  int(a, b) {
    const lo = Math.ceil(a);
    const hi = Math.floor(b);
    if (hi <= lo) return lo;
    return lo + (this.nextU32() % (hi - lo + 1));
  }

  /** True with probability p. */
  chance(p) {
    return this.next() < p;
  }

  /** -1 or +1. */
  sign() {
    return (this.nextU32() & 1) === 0 ? -1 : 1;
  }

  /** Uniform element from an array. Returns undefined for empty arrays. */
  pick(arr) {
    if (!arr || arr.length === 0) return undefined;
    return arr[this.nextU32() % arr.length];
  }

  /**
   * Weighted pick. `weights[i]` corresponds to `arr[i]`; non-positive weights are skipped.
   * Allocation free.
   */
  pickWeighted(arr, weights) {
    let total = 0;
    for (let i = 0; i < arr.length; i++) if (weights[i] > 0) total += weights[i];
    if (total <= 0) return undefined;
    let r = this.next() * total;
    for (let i = 0; i < arr.length; i++) {
      if (weights[i] <= 0) continue;
      r -= weights[i];
      if (r <= 0) return arr[i];
    }
    return arr[arr.length - 1];
  }

  /** Approximately standard-normal via the sum of four uniforms (Irwin–Hall). */
  gauss() {
    const s = this.next() + this.next() + this.next() + this.next();
    return (s - 2) * 1.7320508; // scaled so variance ~= 1
  }

  /** Symmetric jitter in [-amount, amount]. */
  jitter(amount) {
    return (this.next() * 2 - 1) * amount;
  }

  /** In-place Fisher–Yates shuffle. */
  shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = this.nextU32() % (i + 1);
      const t = arr[i];
      arr[i] = arr[j];
      arr[j] = t;
    }
    return arr;
  }

  /**
   * Creates an independent child stream. The label is hashed into the seed so that
   * adding a new consumer never shifts an existing consumer's sequence.
   * This is why construction-time randomness MUST use a labelled fork.
   */
  fork(label) {
    let h = 0x811c9dc5 ^ this.seed;
    for (let i = 0; i < label.length; i++) {
      h ^= label.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return new Rng(h >>> 0, `${this.label}:${label}`);
  }

  /** Serialisable state for replays. */
  state() {
    return [this.s0 >>> 0, this.s1 >>> 0, this.s2 >>> 0, this.s3 >>> 0, this.draws >>> 0];
  }

  setState(arr) {
    this.s0 = arr[0] >>> 0;
    this.s1 = arr[1] >>> 0;
    this.s2 = arr[2] >>> 0;
    this.s3 = arr[3] >>> 0;
    this.draws = (arr[4] || 0) >>> 0;
    return this;
  }

  /** Folds the current state into a 32-bit digest for replay checksums. */
  digest() {
    let h = 0x811c9dc5;
    const s = [this.s0, this.s1, this.s2, this.s3];
    for (let i = 0; i < 4; i++) {
      h = (h ^ s[i]) >>> 0;
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h >>> 0;
  }
}

/**
 * Creates the four canonical streams for a context. All derive from one master seed
 * via labelled forks, so the streams are independent and stable.
 */
export function createRngStreams(masterSeed) {
  const root = new Rng(masterSeed >>> 0, 'root');
  return {
    master: root,
    sim: root.fork('sim'),
    ai: root.fork('ai'),
    fx: root.fork('fx'),
    ui: root.fork('ui'),
  };
}

/**
 * Deterministic value noise — used by materials and weather. Kept here (not in
 * src/materials) because tests and tools need it without three.
 */
export function hash2(x, y, seed = 0) {
  let h = (Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ seed) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 0x2545f491) >>> 0;
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

export function hash3(x, y, z, seed = 0) {
  let h =
    (Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ Math.imul(z | 0, 0x9e3779b1) ^ seed) >>>
    0;
  h ^= h >>> 15;
  h = Math.imul(h, 0x2545f491) >>> 0;
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

function fade(t) {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/** 2D value noise with quintic interpolation. Deterministic for a given seed. */
export function valueNoise2(x, y, seed = 0) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = fade(xf);
  const v = fade(yf);
  const a = hash2(xi, yi, seed);
  const b = hash2(xi + 1, yi, seed);
  const c = hash2(xi, yi + 1, seed);
  const d = hash2(xi + 1, yi + 1, seed);
  return (a + (b - a) * u) * (1 - v) + (c + (d - c) * u) * v;
}

/**
 * Tiling fractal value noise. `period` makes the result seamless, which is what
 * ARCHITECTURE.md §6 means by "periodic procedural noise where tiling is required".
 */
export function fbm2(x, y, octaves = 4, lacunarity = 2, gain = 0.5, period = 0, seed = 0) {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let fx = x;
  let fy = y;
  let per = period;
  for (let o = 0; o < octaves; o++) {
    let sx = fx;
    let sy = fy;
    if (per > 0) {
      sx = ((fx % per) + per) % per;
      sy = ((fy % per) + per) % per;
    }
    sum += valueNoise2(sx, sy, seed + o * 7919) * amp;
    norm += amp;
    amp *= gain;
    fx *= lacunarity;
    fy *= lacunarity;
    per *= lacunarity;
  }
  return norm > 0 ? sum / norm : 0;
}
